import { PropsWithChildren, useCallback, useState } from 'react';
import { ArrowBigDownDash } from 'lucide-react';
import { isBefore } from 'date-fns';

import { DateRange } from '../../types/date-range.type';

import styles from './download-file-button.module.scss';
import DatePicker from './date-picker';

type Props = PropsWithChildren & {
    isDateLimited?: boolean;
    onClick?: (range: DateRange) => void;
};

const DownloadFileButton = ({ children, onClick }: Props) => {
    const [range, setRange] = useState<DateRange>({});
    const [showDatePicker, setShowDatePicker] = useState(false);

    const handleDateSelect = useCallback(
        (date: Date) => {
            if (range.from && !range.to) {
                if (isBefore(date, range.from)) {
                    return setRange({ from: date, to: undefined });
                }
                return setRange((prev) => ({ ...prev, to: date }));
            }

            return setRange({ from: date, to: undefined });
        },
        [range]
    );

    const handleDownload = useCallback(() => {
        if (!range.from || !range.to) return;
        onClick?.(range);
        setRange({});
        setShowDatePicker(false);
    }, [range, onClick]);

    return (
        <div className={styles.container}>
            <button onClick={() => setShowDatePicker(!showDatePicker)}>
                <div className={styles.content}>
                    <ArrowBigDownDash size={18} />
                    {children}
                </div>
            </button>
            {showDatePicker && (
                <DatePicker
                    range={range}
                    onDateSelect={handleDateSelect}
                    onDownload={handleDownload}
                />
            )}
        </div>
    );
};

export default DownloadFileButton;
